import './polyfills/arrayAt';
import { createApp } from 'vue'
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import App from './App.vue'
import { initSentry } from './sentry';
import { initPostHog } from './posthog';
import { initUmami } from './umami';
import router from './router';
import { initTheme, getIsDark } from './composables/useTheme';
import { initControlSize } from './composables/useControlSize';
import { initAutoRecord } from './composables/useAutoRecord';
import { useAuth } from '@/composables/useAuth';
import { setOnSessionExpired } from '@/services/api';
import { flushRecordingCheckpoint } from '@/composables/useFlightRecorder';

import { IonicVue } from '@ionic/vue';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/**
 * Ionic Dark Mode
 * -----------------------------------------------------
 * For more info, please see:
 * https://ionicframework.com/docs/theming/dark-mode
 */

/* @import '@ionic/vue/css/palettes/dark.always.css'; */
import '@ionic/vue/css/palettes/dark.class.css';
/* @import '@ionic/vue/css/palettes/dark.system.css'; */

/* Theme variables */
import './theme/variables.css';

/* Mapbox GL CSS is loaded on demand when a map is shown (see loadMapboxStyles) */

const app = createApp(App)
  .use(IonicVue)
  .use(router);

initSentry(app, router);
initPostHog(app);
initUmami();

// Listen for deep links (OAuth callback on Android)
CapacitorApp.addListener('appUrlOpen', ({ url }) => {
  // Handle flighttrack://auth/callback?access_token=...
  if (url.startsWith('flighttrack://auth')) {
    // Extract the path and query string
    const urlObj = new URL(url.replace('flighttrack://', 'https://'));
    const path = urlObj.pathname;
    const queryString = urlObj.search;

    // Navigate to the auth callback route with the query params
    router.push(`/auth/callback${queryString}`);
  }
});

CapacitorApp.addListener('appStateChange', ({ isActive }) => {
  if (!isActive) {
    flushRecordingCheckpoint();
  }
});

// Apply saved theme before first paint (class must be on <html> for dark.class.css)
initTheme();
initControlSize();
initAutoRecord();

// Kick off auth session restore before the router processes its first navigation.
// The router guard awaits waitForAuthReady(), so this must fire first to avoid a deadlock.
useAuth().init();

setOnSessionExpired(() => {
  const { setUser, logout } = useAuth();
  // Perform a full logout to clear user state, flights, and reset
  // initPromise so the next login starts clean.
  logout().catch(() => {
    // Fallback: at minimum clear the reactive user ref
    setUser(null);
  });
  router.replace('/login');
});

router.isReady().then(async () => {
  app.mount('#app');

  // Configure status bar to match theme when running natively
  if (Capacitor.isNativePlatform()) {
    try {
      const dark = getIsDark();
      await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
      await StatusBar.setBackgroundColor({ color: dark ? '#020617' : '#ffffff' });
    } catch {
      // StatusBar may not be available in all environments
    }
  }
});
